import { useMutation, UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { fetchClient } from "../../API/fetchClient";
import type { Saisie } from "../../types/saisie";

interface UseValidateSaisieOptions
  extends Omit<
    UseMutationOptions<
      Saisie,
      Error,
      { id: number; action: "valider" | "rejeter"; motif?: string }
    >,
    "mutationFn"
  > {}

export const useValidateSaisie = (options: UseValidateSaisieOptions = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess: optionOnSuccess, ...restOptions } = options;

  return useMutation<Saisie, Error, { id: number; action: "valider" | "rejeter"; motif?: string }>({
    mutationFn: async ({ id, action, motif }): Promise<Saisie> => {
      console.log(`[useValidateSaisie] ${action} saisie`, id, motif ? `with motif: ${motif}` : "");

      const body: { action: string; motif?: string } = { action };
      if (motif) {
        body.motif = motif;
      }

      const response = await fetchClient<Saisie>(`/core/saisies/${id}/validation/`, {
        method: "POST",
        body,
      });

      console.log("[useValidateSaisie] Response:", response);

      if (response.error) {
        console.error("[useValidateSaisie] API Error:", response.error);

        // Handle validation errors
        if (response.error.non_field_errors) {
          throw new Error(response.error.non_field_errors.join(", "));
        }

        if (response.error.detail) {
          throw new Error(response.error.detail);
        }

        // Generic error
        throw new Error(`Failed to ${action} saisie: ${JSON.stringify(response.error)}`);
      }

      if (!response.data) {
        throw new Error(`No data received from ${action} saisie`);
      }

      console.log(`[useValidateSaisie] Successfully ${action}ed saisie:`, response.data);
      return response.data;
    },
    onSuccess: async (data, variables, onMutateResult, context) => {
      // Immediately update the saisie in all list caches so the UI moves it
      // to the correct panel (validated/rejected) without waiting for a refetch
      queryClient.setQueriesData<Saisie[]>({ queryKey: ["saisies"] }, (old) => {
        if (!old) return old;
        return old.map((s) => (s.id === data.id ? data : s));
      });
      // Update the single-item cache
      queryClient.setQueryData(["saisie", data.id], data);
      // Background sync to keep server state consistent
      queryClient.invalidateQueries({ queryKey: ["saisies"] });
      // Run caller's onSuccess if provided
      if (optionOnSuccess) {
        await optionOnSuccess(data, variables, onMutateResult, context);
      }
    },
    ...restOptions,
  });
};

export default useValidateSaisie;
